interface HTMLElementObject {
  tagName: string;
  attributes: { [key: string]: string };
  children: (HTMLElementObject | { text: string })[];
}

export const html = {
  htmlStringToObject(htmlString: string): any {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    function elementToObject(element: Element): HTMLElementObject {
      const obj: HTMLElementObject = {
        tagName: element.tagName.toLowerCase(),
        attributes: {},
        children: [],
      };

      // Add attributes
      for (let i = 0; i < element.attributes.length; i++) {
        const attr = element.attributes[i];
        obj.attributes[attr.name] = attr.value;
      }

      // Recursively add children
      for (let i = 0; i < element.childNodes.length; i++) {
        const child = element.childNodes[i];
        if (child.nodeType === Node.ELEMENT_NODE) {
          obj.children.push(elementToObject(child as Element));
        } else if (child.nodeType === Node.TEXT_NODE && child.nodeValue?.trim()) {
          obj.children.push({ text: child.nodeValue.trim() });
        }
      }
      return obj;
    }
    return elementToObject(doc.body.firstElementChild as Element);
  },
  objectToHtmlString(obj: HTMLElementObject): string {
    // Convert attributes object to a string
    function attributesToString(attributes: { [key: string]: string }): string {
      return Object.entries(attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(" ");
    }

    // Recursively build the HTML string from the object
    function elementToHtml(element: HTMLElementObject): string {
      const { tagName, attributes, children } = element;

      // Create the opening tag with attributes
      const attributesString = attributesToString(attributes);
      const openingTag = attributesString ? `<${tagName} ${attributesString}>` : `<${tagName}>`;

      // Create the children as a string
      const childrenString = children.map((child) => ("text" in child ? child.text : elementToHtml(child))).join("");

      // Create the closing tag
      const closingTag = `</${tagName}>`;

      // Return the full HTML string
      return `${openingTag}${childrenString}${closingTag}`;
    }

    // Convert the root object to an HTML string
    return elementToHtml(obj);
  },
  getTreeData(data: HTMLElementObject, minLength: number = 50) {
    let found = false;
    function findNodeWithMinLength(arr: any[], parentNode?: HTMLElementObject): HTMLElementObject[] {
      return arr.reduce((acc, current) => {
        if (found) return acc;
        if (current?.children?.length > 0 && !found) {
          return [...acc, { ...current, children: findNodeWithMinLength(current.children, current) }];
        }
        if (current?.text?.length > minLength && !found) {
          found = true;
          return [
            ...acc,
            {
              tagName: "div",
              ...parentNode,
              attributes: { class: `truncate max-w-12rem ${parentNode?.attributes?.class ?? ""}` },
              children: [{ text: current.text }],
            },
          ];
        }
        return [...acc, current];
      }, []);
    }
    return findNodeWithMinLength([data])[0];
  },
};
